import { IntlShape, MessageDescriptor } from 'react-intl'

import { Programs } from './programConfigs'
import { getCandidateCTPOsForTreatmentOption } from './treatmentOptionProgramTaxonomyMappings'
import { ProgramConfigDisplayLabels, ProgramTaxonomyType } from './types'
import { Episode } from '../../models/episodes/Episodes'
import { ProgramNames } from '../../models/programs/Programs'
import { ProviderProgramTaxonomy } from '../../models/providers/Providers'
import { BRANCH_APP_LINK } from '../common/constants/constants'
import { DirectLinkIntent, ProgramCustomerPropertyId } from '../common/constants/customerProgram'
import { TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'
import { DirectLinkSource } from '../searchForCare/constants'

/**
 * Converts a program ID to the program name.
 *
 * This is the internal name for the program and can be used as a stable identifier.
 * For user-facing display labels, use `getProgramDisplayLabel`.
 */
export function getProgramNameFromId(programId: string | null | undefined): ProgramNames | undefined {
  return programId ? Programs.getProgramConfigById(programId)?.programName : undefined
}

export function getProgramIdFromName(programName: string | undefined): string | undefined {
  return Programs.getProgramConfigByName(programName)?.blendedCareProgramId ?? undefined
}

export function isCoachingProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isCoachingProgram ?? false
}

/**
 * An episode's `program_name` comes from the LyraTherapy service, which registers neuropsych as
 * `NeuroPsychEvals` rather than the member-facing `ProgramNames.NeuroPsychologicalEvaluations`.
 * Every other program uses the same string on both sides; neuropsych is the exception.
 */
const LT_NEUROPSYCH_EVALUATION_PROGRAM_NAME = 'NeuroPsychEvals'

export function isNeuropsychEvaluationProgram(name: ProgramNames | string | undefined): boolean {
  return name === LT_NEUROPSYCH_EVALUATION_PROGRAM_NAME || name === ProgramNames.NeuroPsychologicalEvaluations
}

export function isDAEngageProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isDAEngageProgram ?? false
}

export function isDAMinorProgram(name: ProgramNames | string | undefined): boolean {
  return isDAEngageProgram(name) && isMinorProgram(name)
}

export function isChildProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isChildProgram ?? false
}

export function isTeenProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isTeenProgram ?? false
}

export function isMinorProgram(name: ProgramNames | string | undefined): boolean {
  return isChildProgram(name) || isTeenProgram(name)
}

export function supportsGuardianMessaging(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.supportsGuardianMessaging ?? false
}

export function isEpisodeInsightsProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.supportsEpisodeInsights ?? false
}

export function isLicenseBasedProgram(name: ProgramNames | string | undefined): boolean {
  return !!Programs.getProgramConfigByName(name)?.licenseProgramType
}

export function getLicenseProgramType(name: ProgramNames | string | undefined): string | undefined {
  return Programs.getProgramConfigByName(name)?.licenseProgramType ?? undefined
}

/**
 * Whether a selected treatment option belongs to a seat-license program (e.g. Manager Coaching).
 *
 * License-based programs are not triage outcomes, so they intentionally declare no
 * `associatedTriageTreatmentOptions` and `convertTriageTreatmentOptionToProgram` returns undefined for them.
 * Adding them there would pull them into EAP session-limit and HPI logic.
 */
export function isLicenseBasedTreatmentOption(
  treatmentOption: TREATMENT_OPTIONS_TYPE | string | null | undefined,
): boolean {
  if (!treatmentOption) return false
  const candidateCTPOs = getCandidateCTPOsForTreatmentOption(treatmentOption)
  if (candidateCTPOs.length === 0) return false
  return Programs.getProgramConfigsMatching((c) => !!c.licenseProgramType).some((program) =>
    candidateCTPOs.some((ctpo) => isCTPOForProgram(ctpo, program.programName)),
  )
}

export type UserConsent = {
  programName: string
  consentText: MessageDescriptor
  consentUrl: string
}

export function getConsentsInfoForUser(episodes: Episode[], isUserInternational: boolean): UserConsent[] {
  const userEpisodePrograms = [...new Set(episodes.map((episode) => episode.program_name))]

  return userEpisodePrograms.flatMap((name) => {
    const consents = Programs.getProgramConfigByName(name)?.consents ?? []
    return consents
      .filter(({ showConsentIf }) => showConsentIf({ isInternational: isUserInternational }))
      .map(({ consentText, consentUrl }) => ({
        programName: name,
        consentText,
        consentUrl,
      }))
  })
}

/* Never use this pattern of looping through all programs to match CTPO and this is only done here to support
  PerformanceCoaching program which is under Coaching programName but with different CTPO values and we want
  to hide client goals for PerformanceCoaching program but display for Coaching program. */
// TODO: When performance coaching is its own program, replace the contents of this function with
// return Programs.getProgramConfigByName(programName)?.shouldHideClientGoals ?? false
export function shouldHideClientGoalsForCTPO(ctpo?: Partial<ProviderProgramTaxonomy> | null): boolean {
  if (!ctpo) return false
  return Programs.getProgramConfigsMatching((c) => !!c.shouldHideClientGoals).some((c) =>
    isCTPOForProgram(ctpo, c.programName),
  )
}

export function isCTPOForProgram(
  ctpo?: Partial<ProviderProgramTaxonomy> | null,
  programName?: ProgramNames | string,
): boolean {
  if (!ctpo || !programName) return false
  const programCTPO = Programs.getProgramConfigByName(programName)?.directBookingParams
  if (!programCTPO) return false
  return (
    ctpo.clientele === programCTPO.clientele &&
    ctpo.treatment === programCTPO.treatment &&
    ctpo.offering === programCTPO.offering &&
    ctpo.partner === programCTPO.partner
  )
}

export function isCustomerProgramSelfPayEligible(customerPropertyId: string | undefined): boolean {
  return Programs.getProgramsByCustomerPropertyId(customerPropertyId).some((program) => program.selfPayEligible)
}

export function isHPIScreenDisabled(
  customerPropertyId: string | undefined,
  shouldSurfaceNPEvalHPIScreen: boolean,
): boolean {
  if (customerPropertyId === ProgramCustomerPropertyId.neuropsychologicalEvaluations && !shouldSurfaceNPEvalHPIScreen) {
    return true
  }

  return Programs.getProgramsByCustomerPropertyId(customerPropertyId).some((program) => program.hpiScreenDisabled)
}

/**
 * Returns the display label string for a program.
 *
 * Accepts a single variant or an array of variants as a fallback chain.
 * When an array is provided, variants are tried in order and the first
 * defined value is returned. Falls back to `default` if no variant matches.
 *
 * @example
 * getProgramDisplayLabel(intl, programName, 'short')
 * getProgramDisplayLabel(intl, programName, ['daClientList', 'short'])
 *
 * See `ProgramConfigDisplayLabels` for details on each variant.
 * Only `default` and `careType` variants are internationalized.
 */
export function getProgramDisplayLabel(
  intl: IntlShape,
  programName: ProgramNames | string | undefined,
  variant: keyof ProgramConfigDisplayLabels | ReadonlyArray<keyof ProgramConfigDisplayLabels> = 'default',
): string {
  const displayLabels = Programs.getProgramConfigByName(programName)?.displayLabels
  if (!displayLabels) {
    return ''
  }

  const variants: (keyof ProgramConfigDisplayLabels)[] = Array.isArray(variant) ? variant : [variant]

  for (const v of variants) {
    if (v === 'default') {
      return intl.formatMessage(displayLabels.default)
    }
    if (v === 'careType') {
      const label = displayLabels.careType
      if (label != undefined) {
        return intl.formatMessage(label)
      }
      continue
    }
    const label = displayLabels[v]
    if (label != undefined) {
      return label
    }
  }

  return intl.formatMessage(displayLabels.default)
}

/**
 * Returns the base direct booking CTPO params for a program, looked up by program name.
 */
export function getDirectBookingParamsByProgramName(
  programName: ProgramNames | string | undefined,
): ProgramTaxonomyType | undefined {
  if (!programName) {
    return undefined
  }
  return Programs.getProgramConfigByName(programName)?.directBookingParams
}

/**
 * Builds a direct path booking deep link URL for a program.
 *
 * The base CTPO is read from `ProgramConfig.directBookingParams`. Callers supply
 * contextual overrides (intent, source) and a fallback URL.
 *
 * @returns The full deep link URL, or `undefined` if the program has no `directBookingParams`.
 */
export function buildDirectBookingLink({
  programName,
  directLinkIntent,
  directLinkSource,
  fallbackUrl,
}: {
  programName: ProgramNames | string
  directLinkIntent?: DirectLinkIntent
  directLinkSource?: DirectLinkSource
  fallbackUrl?: string
}): string | undefined {
  const params = getDirectBookingParamsByProgramName(programName)

  if (!params) return undefined

  const { clientele, treatment, partner, offering } = params

  const searchParams = new URLSearchParams()
  searchParams.set('directPath', 'true')
  searchParams.set('clientele', clientele)
  searchParams.set('treatment', treatment)
  searchParams.set('partner', partner)
  searchParams.set('offering', offering)

  if (directLinkIntent) {
    searchParams.set('directLinkIntent', directLinkIntent)
  }
  if (directLinkSource) {
    searchParams.set('directLinkSource', directLinkSource)
  }
  if (fallbackUrl) {
    searchParams.set('$fallback_url', fallbackUrl)
  }

  return `${BRANCH_APP_LINK}directPathBooking?${searchParams.toString()}`
}
